import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import UserProfile from './UserProfile';
import OnboardingModal from './OnboardingModal';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check if user needs onboarding
    if (user && !user.is_onboarded) {
      setShowOnboarding(true);
    }
  }, [user]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };


  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Navigation - Restored shuffle.dev design */}
      <div className="flex flex-col fixed top-0 left-0 z-50 w-80 h-full bg-coolGray-900 text-white overflow-y-auto">
                {/* Logo Section with Navigation */}
        <div className="p-10 bg-coolGray-900 flex flex-col h-full">
          <div className="flex justify-center">
            <img src="/salesforcelogo-square-120.png" alt="Salesforce Demo Hub" className="h-36 w-auto" />
          </div>
          
          {/* Navigation Content */}
          <div className="mt-8 pr-2.5 flex flex-col flex-1">
            <ul className="mb-8">
                          <li>
              <a
                href="/"
                className="p-3 py-4 flex items-center text-coolGray-600 hover:text-green-500 hover:bg-coolGray-800 rounded-md transition-colors"
              >
                <div className="flex items-center">
                  <svg className="mr-2" width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" fill="currentColor" />
                  </svg>
                  <p className="text-white font-medium text-base">Dashboard</p>
                </div>
              </a>
            </li>
                                                        <li>
                              <a
                                href="/my-demos"
                                className="p-3 py-4 flex items-center text-coolGray-600 hover:text-green-500 hover:bg-coolGray-800 rounded-md transition-colors"
                              >
                                <div className="flex items-center">
                                  <svg className="mr-2" width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" fill="currentColor" />
                                  </svg>
                                  <p className="text-white font-medium text-base">My Demo Plans</p>
                                </div>
                              </a>
                            </li>
                                                          <li>
                              <a
                                href="/shared-demos"
                                className="p-3 py-4 flex items-center text-coolGray-600 hover:text-green-500 hover:bg-coolGray-800 rounded-md transition-colors"
                              >
                                <div className="flex items-center">
                                  <svg className="mr-2" width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" />
                                  </svg>
                                  <p className="text-white font-medium text-base">Shared Demo Plans</p>
                                </div>
                              </a>
                            </li>
                                                      <li>
                              <a
                                href="/demos/new"
                                className="p-3 py-4 flex items-center text-coolGray-600 hover:text-green-500 hover:bg-coolGray-800 rounded-md transition-colors"
                              >
                                <div className="flex items-center">
                                  <svg className="mr-2" width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" fill="currentColor" />
                                  </svg>
                                  <p className="text-white font-medium text-base">New Demo Plan</p>
                                </div>
                              </a>
                            </li>
                            <li>
                <a
                  href="/analytics"
                  className="p-3 py-4 flex items-center text-coolGray-600 hover:text-green-500 hover:bg-coolGray-800 rounded-md transition-colors"
                >
                  <div className="flex items-center">
                    <svg className="mr-2" width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" fill="currentColor" />
                    </svg>
                    <p className="text-white font-medium text-base">Analytics</p>
                  </div>
                </a>
              </li>
            </ul>
            
            {/* Spacer to push help and user profile to bottom */}
            <div className="flex-1"></div>
            
            {/* Help Section */}
            <div className="px-4 pb-4">
              <a
                href="/help"
                className="p-3 py-4 flex items-center text-coolGray-600 hover:text-green-500 hover:bg-coolGray-800 rounded-md transition-colors"
              >
                <div className="flex items-center">
                  <svg className="mr-2" width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" fill="currentColor" />
                  </svg>
                  <p className="text-white font-medium text-base">Help</p>
                </div>
              </a>
            </div>
            
            {/* User Profile Section */}
            <div className="px-4 pb-8">
              <UserProfile />
            </div>
          </div>
        </div>
            </div>


      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden ml-80">

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          {children}
        </main>
      </div>

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
    </div>
  );
};

export default Layout;
